/*
 * Copyright (c) 2014-2024 Comparatio USA  LLC. <https://www.comparatio.com>
 */

import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Page} from '../../model/page';
import {ActivatedRoute, ActivatedRouteSnapshot, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {permissions} from '../../permissions';
import * as _ from 'lodash';
import {OrderHeadersService} from '../../services/order-headers.service';
import {ConfirmationService} from 'primeng/api';
import {MatDialog} from '@angular/material/dialog';
import {ExportModalComponent} from '../../components/export-modal';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs/Subscription';

@Component({
    templateUrl: './order-headers.component.html',
    styleUrls: ['./order-headers.component.scss'],
    providers: [
        OrderHeadersService,
        ConfirmationService
    ]
})
export class OrderHeadersComponent implements OnInit, OnDestroy {

    @ViewChild('myTable') table: any;

    public loading = false;
    public permissions = permissions;

    page = new Page();
    rows = new Array<object>();
    cols = [];
    totalRecords = 0;
    public selected: any = null;

    private filters: { [key: string]: any; } = {};
    public viewFilters: boolean;
    public filtersApplied: boolean;
    private subscriptions: Array<Subscription>;

    private vendors = [];
    filteredVendors = [];
    private headerStatuses = this.orderHeadersService.getStatuses();
    private refOrderHeadersService = OrderHeadersService;
    private acknowledgeFlags: Array<object> = [
        {value: null, text: 'All'},
        {value: '1',  text: 'Acknowledged'},
        {value: '0',  text: 'Not Acknowledged'}
    ];
    private confirmFlags: Array<object> = [
        {value: null, text: 'All'},
        {value: '1',  text: 'Confirmed'},
        {value: '0',  text: 'Not Confirmed'}
    ];

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private orderHeadersService: OrderHeadersService,
        private confirmationService: ConfirmationService,
        private toastr: ToastrService,
        public  dialog: MatDialog,
        private title: Title
    ) {
        this.page.pageNumber = 0;
        this.page.size = 50;
        this.title.setTitle('EDI - Purchase Orders');
    }

    ngOnInit() {
        this.filtersApplied = false;
        this.viewFilters = false;
        this.cols = [
            {
                field: 'EXPANSION', header: '', visible: true,
                align: 'center', width: '35px', export: {visible: false, checked: false}
            }, {
                field: 'PO1_STATUS', header: 'Status', visible: true,
                align: 'left', width: '125px', export: {visible: true, checked: true}
            }, {
                field: 'PO1_NUMBER', header: 'PO Number', visible: true,
                align: 'left', width: '125px', export: {visible: true, checked: true}
            }, {
                field: 'CO1_NAME', header: 'Company', visible: true,
                align: 'left', width: '150px', export: {visible: true, checked: true}
            }, {
                field: 'VENDOR_NAME', header: 'Vendor', visible: true,
                align: 'left', width: '175px', export: {visible: false, checked: false}
            }, {
                field: 'PO1_ACKNOWLEDGED', header: 'ACK', visible: true,
                align: 'center', width: '50px', export: {visible: true, checked: true}
            }, {
                field: 'PO1_CONFIRMED', header: 'CNF', visible: true,
                align: 'center', width: '50px', export: {visible: true, checked: true}
            }, {
                field: 'PO1_LINE_COUNT', header: 'Lines', visible: true,
                align: 'right', width: '75px', export: {visible: true, checked: true}
            }, {
                field: 'PO1_EXTENDED_VALUE', header: 'Total Value', visible: true,
                align: 'right', width: '125px', export: {visible: true, checked: true}
            }, {
                field: 'PO1_CURRENCY', header: 'Currency', visible: true,
                align: 'center', width: '75px', export: {visible: true, checked: true}
            }, {
                field: 'PO1_CREATED_ON', header: 'Creation', visible: true,
                align: 'right', width: '100px', export: {visible: true, checked: true}
            }, {
                field: 'PO1_DUE_DATE', header: 'Due Date', visible: true,
                align: 'right', width: '100px', export: {visible: true, checked: true}
            }, {
                field: 'PO1_SHIP_TO_NAME', header: 'Ship To', visible: true,
                align: 'left', width: '125px', export: {visible: true, checked: true}
            }, {
                field: 'PO1_SHIP_VIA_INFO', header: 'Ship Via', visible: true,
                align: 'left', width: '75px', export: {visible: true, checked: true}
            }, {
                field: 'ACTIONS', header: '', visible: true,
                align: 'center', width: '100px', export: {visible: false, checked: false}
            }
        ];

        const filterParams = this.route.snapshot.queryParams;
        this.parseParams( filterParams );

        this.subscriptions = [];
        this.subscriptions.push(
            this.orderHeadersService.preloadData().subscribe(data => {
                this.vendors = data.vendors.map(vendor => {
                    return {
                        CO1_ID      : vendor.CO1_ID,
                        VD1_ID      : vendor.VD1_ID,
                        VENDOR_ID   : vendor.VENDOR_ID,
                        VENDOR_NAME : vendor.VD1_NAME
                    };
                });
                this.getData(false);
            })
        );
    }

    ngOnDestroy() {
        if (this.subscriptions.length > 0) {
            for (let index = 0; index < this.subscriptions.length; index++) {
                this.subscriptions[index].unsubscribe();
            }
        }
    }

    parseParams( filterParams ) {
        if (filterParams !== undefined && filterParams !== null) {
            let showFilters = false;
            for (const key of Object.keys(filterParams)) {
                const value = filterParams[key];
                if (value && value.toString() !== '') {
                    if (key.toString() === 'page') {
                        this.page.pageNumber = Number(value);
                    } else if (key.toString() === 'perPage') {
                        this.page.size = Number(value);
                    } else if (key.toString() === 'PO1_STATUS') {
                        this.page.filterParams[key] = Number(value);
                        this.filters[key.toString()] = Number(value);
                        showFilters = true;
                    } else {
                        this.page.filterParams[key] = value;
                        this.filters[key.toString()] = value.toString();
                        showFilters = true;
                    }
                }
            }
            this.viewFilters = showFilters;
            this.filtersApplied = showFilters;
        }
    }

    autoCompleteVendorName(filterValue) {
        this.filteredVendors = filterValue ? this._filterVendors(filterValue) : JSON.parse(JSON.stringify(this.vendors));
    }

    getData(reloadWithParams = true) {
        this.setPage({offset: this.page.pageNumber}, reloadWithParams);
    }

    loadLines(po_id) {
        this.router.navigate(['order-headers/' + po_id], {queryParams: {keepFilters: 1}});
    }

    setPage(pageInfo: { offset: any; }, reloadWithParams = true) {
        if (reloadWithParams) {
            this.page.filterParams['page'] = pageInfo.offset;
            this.page.filterParams['perPage'] = this.page.size;
            this.router.routeReuseStrategy.shouldReuseRoute = function (future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot) {
                return false;
            }
            this.router.navigate([], { relativeTo: this.route, queryParams: this.page.filterParams });
        } else {
            this.loading = true;
            this.subscriptions.push(
                this.orderHeadersService.getItems(this.page).subscribe(result => {
                    this.rows = result.data.map(orderHeader => {
                        if (orderHeader['PO1_DUE_DATE'].length > 10) {
                            orderHeader['PO1_DUE_DATE'] = orderHeader['PO1_DUE_DATE'].substring(0, 10);
                            orderHeader['PO1_SHIP_VIA_INFO'] = orderHeader['PO1_SHIP_VIA_INFO'] ? orderHeader['PO1_SHIP_VIA_INFO'] : '';
                        }
                        return orderHeader;
                    });
                    this.totalRecords = result.count;
                    this.loading = false;
                }, () => {
                    this.loading = false;
                    window.dispatchEvent(new Event('resize'));
                    this.toastr.error('Request timed out', 'Service Error', {
                        timeOut: 3000,
                    });
                })
            );
        }
    }

    onPage(event) {
        this.page.size = event.rows;
        this.setPage({
            offset: event.first / event.rows
        });
    }

    onSubmit() {
        const filters = _.clone(this.filters);
        if (filters['CO1_ID'] && parseInt(filters['CO1_ID'], 10) === -1) {
            filters['CO1_ID'] = null;
        }
        for (const filterKey of Object.keys(filters)) {
            if (filters[filterKey] == null || filters[filterKey] === undefined || filters[filterKey] === '') {
                delete filters[filterKey];
            }
        }
        this.filtersApplied = Object.keys(filters).length > 0;
        this.page.filterParams = filters;
        this.getData();
    }

    clearFilters() {
        this.filters = {};
        this.viewFilters = false;
        this.onSubmit();
    }

    toggleFilterView() {
        const filters = _.clone(this.filters);
        let validFilterPresent = false;
        if (Object.keys(filters).length > 0) {
            for (const key of Object.keys(filters)) {
                if (filters[key]) {
                    validFilterPresent = true;
                }
            }
        }
        if (!validFilterPresent) {
            this.viewFilters = !this.viewFilters;
        }
    }

    _filterVendors(value: string) {
        const filterValue = value.toUpperCase();
        const vendors = JSON.parse(JSON.stringify(this.vendors));
        return vendors.filter(vendor => vendor.VENDOR_NAME.toUpperCase().indexOf(filterValue) === 0);
    }

    deleteRecord() {
        if (this.selected && this.selected[0] && this.selected[0]['PO1_ID']) {
            this.confirmationService.confirm({
                message: 'Are you sure that you want to perform this action?',
                accept: () => {
                    this.loading = true;
                    const PO1_IDs = this.selected.map( item => item['PO1_ID'] );
                    this.subscriptions.push(
                        this.orderHeadersService.deleteMultiple(PO1_IDs).subscribe(result => {
                            this.setPage({
                                offset: this.page.pageNumber
                            });
                        }, () => {
                            this.loading = false;
                            this.toastr.error('On delete PO error occurred', 'Service Error', {
                                timeOut: 3000,
                            });
                        })
                    );
                }
            });
        }
    }

    exportClick() {
        const dialogRef = this.dialog.open(ExportModalComponent, {
            width: window.innerWidth - 100 + 'px',
            height: window.innerHeight - 80 + 'px',
            role: 'dialog',
            data: {cols: this.cols, modeEnabled: 0},
        });

        dialogRef.beforeClose().subscribe(preferences => {
            if (preferences) {
                this.orderHeadersService.export(preferences.columns, this.page, 'PurchaseOrders.xls').subscribe();
            }
        });
    }
}




// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/order-headers/order-headers.component.ts